import { useTranslation } from 'react-i18next';
import { cn, Flex, Grid, Icon, Markdown, Text } from '@kognitos/lattice';

import type { V1Step } from '@/generated/hooks/grimoire/models';
import type { TStepRunStatus } from '@/modules/automation/utils/step-run-status';

const RULE_TEXT_PROPS = { level: 'small', color: 'muted' } as const;

/** Props for {@link DraftStepNode}. */
export interface IDraftStepNodeProps {
  number?: number;
  status?: TStepRunStatus;
  selected: boolean;
  /** The hovered step belongs to this step's phase group. */
  inHoveredGroup?: boolean;
  /** Overrides the document's test id, for a badge drawn somewhere else. */
  testId?: string;
}

/**
 * A draft step's number badge, which turns into its run status once it has
 * one.
 *
 * @param props - The step's number, status and selection
 * @returns The badge
 */
export function DraftStepNode({
  number,
  status,
  selected,
  inHoveredGroup = false,
  testId,
}: IDraftStepNodeProps) {
  const statusIcon =
    status === 'running' ? (
      <Icon type="LoaderCircle" size="xs" className="animate-spin" />
    ) : status === 'done' ? (
      <Icon type="Check" size="xs" />
    ) : status === 'failed' ? (
      <Icon type="X" size="xs" className="text-destructive" />
    ) : status === 'paused' ? (
      <Icon type="AlertCircle" size="xs" className="text-warning" />
    ) : (
      number
    );

  return (
    <Flex
      align="center"
      justify="center"
      data-testid={testId ?? `draft-document-step-node-${number}`}
      className={cn(
        'relative z-10 size-6 shrink-0 rounded-full border border-border bg-card text-xs font-medium tabular-nums text-muted-foreground transition-[border-color,border-width] duration-300 ease-in-out',
        status !== 'done' && 'draft-node-hoverable',
        inHoveredGroup && status !== 'done' && 'draft-selection-ring border-2',
        selected &&
          status !== 'done' &&
          'draft-selection-ring border-2 text-foreground',
        status === 'done' &&
          'border-success bg-success text-success-foreground',
      )}
    >
      {statusIcon}
    </Flex>
  );
}

/** Props for {@link DraftStepDescription} and {@link DraftStepRules}. */
export interface IDraftStepDescriptionProps {
  step: V1Step;
  /** A selected step's rules sit on the card colour, over the selection fill. */
  selected: boolean;
}

/**
 * A draft step's rules, each in its own box.
 *
 * @param props - The step and whether it is selected
 * @returns The rules, or nothing for a step without any
 */
export function DraftStepRules({ step, selected }: IDraftStepDescriptionProps) {
  return (
    <>
      {(step.rules ?? []).map((rule, index) => (
        <Flex
          // eslint-disable-next-line react-x/no-array-index-key -- reason: rules are a static, never-reordered list, and two identical rule strings are easy for a generated manifest to produce
          key={`${step.number}-${index}-${rule}`}
          className={cn(
            'my-0.5 mt-2 max-w-[600px] rounded-md bg-muted/45 px-3 py-2 italic transition-colors group-hover/step:bg-card',
            selected && 'bg-card',
          )}
        >
          <Markdown textProps={RULE_TEXT_PROPS}>{rule}</Markdown>
        </Flex>
      ))}
    </>
  );
}

/**
 * A draft step's description in full: its overview, then each rule in its own
 * box.
 *
 * @param props - The step and whether it is selected
 * @returns The description
 */
export function DraftStepDescription({
  step,
  selected,
}: IDraftStepDescriptionProps) {
  return (
    <>
      {step.overview && (
        <Text level="small" className="max-w-[620px] leading-6">
          {step.overview}
        </Text>
      )}
      <DraftStepRules step={step} selected={selected} />
    </>
  );
}

/** Props for {@link DraftStepSummary}. */
export interface IDraftStepSummaryProps {
  step: V1Step;
  selected?: boolean;
  /** Leave the overview and rules to a host that shows them elsewhere. */
  showDescription?: boolean;
  /** Test id for the number badge, so it doesn't clash with the document's. */
  nodeTestId?: string;
}

/**
 * A step as the draft document shows it — number badge, title, overview and
 * rules, all of it with nothing folded away — for surfaces that show one step
 * on its own, such as the step rail beside a map. The badge keeps the number
 * there: the map's own tile already says how the step went.
 *
 * @param props - The step, whether it reads as selected, and whether to show its description
 * @returns The summary
 */
export function DraftStepSummary({
  step,
  selected = false,
  showDescription = true,
  nodeTestId,
}: IDraftStepSummaryProps) {
  const { t } = useTranslation('automation');

  return (
    <Grid
      className="grid-cols-[24px_1fr] gap-x-4"
      data-testid="draft-step-summary"
    >
      <DraftStepNode
        number={step.number}
        selected={selected}
        testId={nodeTestId}
      />
      <Flex direction="column" gap={0.5} className="min-w-0 select-text">
        <Flex align="start" gap={2} className="min-w-0">
          <Text level="small" weight="medium" className="flex-1 leading-6">
            {step.title || t('automation.manifestSopView.step.untitled')}
          </Text>
        </Flex>
        {showDescription && (
          <DraftStepDescription step={step} selected={selected} />
        )}
      </Flex>
    </Grid>
  );
}
